import React, { useState, useEffect } from 'react';
import { api } from '@services/api';

import {
  Container,
  TextField,
  Typography,
  Grid,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Chip,
  Card,
  CardContent,
  CardActions,
  IconButton,
  Tooltip,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress
} from '@material-ui/core';

import { Autocomplete } from '@material-ui/lab';
import MuiAlert, { type Color } from '@material-ui/lab/Alert';

import { Upload } from '@components/Upload';
import { FileList } from '@components/FileList';
import { useFiles } from '@base/context/files';
import { Button } from '@components/Button';
import { Plus, Pencil, Trash, Eye } from '@phosphor-icons/react/dist/ssr';
import {
  DivBenefits,
  DivButtons,
  DivGridFieldDataContract,
  DivServices,
  PaperContainer,
  PlanosGrid,
  PlanoCard as StyledPlanoCard
} from './styles';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm, useFieldArray } from 'react-hook-form';
import { Exames } from '@components/Exames';
import { Servicos } from '@components/Servicos';
import { Toxicologico } from '@components/Toxicologico';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@context/auth';
import { AppError } from '@utils/AppError';
import { NavLink } from '@components/NavLink';

interface IRiscos {
  id_risco: string | number;
  nome: string;
}

interface IExame {
  id_exames: string | number;
  nome: string;
  valor?: number | string;
}

interface IServico {
  id_servicos: string | number;
  nome: string;
  valor?: number | string;
}

interface IToxicologico {
  id_exame_tox: string | number;
  nome: string;
  valor?: number | string;
}

interface IPlano {
  id?: string;
  nome: string;
  valor: number | string;
  exames_fixos: IExame[];
  exames_adicionais: IExame[];
  servicos: IServico[];
  exames_toxicologicos?: IToxicologico[];
  riscos?: IRiscos[];
}

const planoShema = z.object({
  id: z.string().optional(),
  nome: z.string().min(1, 'Nome do plano é obrigatório'),
  valor: z.union([
    z.number().min(0, 'Valor deve ser maior ou igual a zero'),
    z.string().min(1, 'Valor é obrigatório')
  ]),
  exames_fixos: z.array(z.object({
    id_exames: z.union([z.string(), z.number()]),
    nome: z.string(),
    valor: z.union([z.number(), z.string()]).optional()
  })),
  exames_adicionais: z.array(z.object({
    id_exames: z.union([z.string(), z.number()]),
    nome: z.string(),
    valor: z.union([z.number(), z.string()])
  })),
  servicos: z.array(z.object({
    id_servicos: z.union([z.string(), z.number()]),
    nome: z.string(),
    valor: z.union([z.number(), z.string()]).optional()
  })),
  exames_toxicologicos: z.array(z.object({
    id_exame_tox: z.union([z.string(), z.number()]),
    nome: z.string(),
    valor: z.union([z.number(), z.string()])
  })).optional(),
  riscos: z.array(z.object({
    id_risco: z.union([z.string(), z.number()]),
    nome: z.string()
  })).optional()
});

const contractShema = z.object({
  nome_contrato: z.string().trim().optional().default(''),
  cod_contrato: z
    .string()
    .trim()
    .min(1, { message: 'O campo não pode ser vazio.' }),
  data_criacao: z.string().optional(),
  data_renovacao: z.string().optional(),
  arquivo: z.array(z.string()).optional(),
  planos: z.array(planoShema).max(1, 'O contrato pode ter apenas um plano'),
  risco: z.array(
    z.object({
      id_risco: z.string().or(z.number()).optional(),
      nome: z.string()
    })
  ).optional(),
  num_conv_med: z.string().optional(),
  num_conv_odont: z.string().optional(),
  num_conv_acade: z.string().optional(),
  num_seg_vida: z.string().optional(),
  num_apol_pat: z.string().optional(),
  usuario: z.string().optional()
});

export type ContractSaveFormData = z.infer<typeof contractShema>;
export type PlanoFormData = z.infer<typeof planoShema>;

const OPCOES_PLANOS = [
  { nome: 'Bronze', descricao: 'Plano básico com benefícios essenciais' },
  { nome: 'Prata', descricao: 'Plano intermediário com mais benefícios' },
  { nome: 'Ouro', descricao: 'Plano completo com benefícios premium' },
  { nome: 'Platina', descricao: 'Plano máximo com todos os benefícios' }
];

function useLazyFetch<T>(endpoint: string) {
  const [data, setData] = useState<T[]>([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const fetchData = async () => {
    if (loaded) return;
    
    setLoading(true);
    try {
      const res = await api.get(endpoint);
      setData(res.data);
      setLoaded(true);
    } catch (error) {
      console.error(`Erro ao carregar ${endpoint}:`, error);
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  return { data, loading, fetchData, loaded };
}

function PlanoCard({ 
  plano, 
  onEdit, 
  onDelete,
  dataCriacao,
  dataRenovacao
}: { 
  plano: PlanoFormData; 
  onEdit: () => void; 
  onDelete: () => void;
  dataCriacao?: string;
  dataRenovacao?: string;
}) {
  const calcularFidelidade = () => {
    if (!dataCriacao || !dataRenovacao) return 'Sem fidelidade';
    
    const hoje = new Date();
    const dataCriacaoObj = new Date(dataCriacao);
    const dataRenovacaoObj = new Date(dataRenovacao);
    
    if (hoje >= dataCriacaoObj && hoje <= dataRenovacaoObj) {
      return 'Em período de fidelidade';
    } else if (hoje > dataRenovacaoObj) {
      return 'Fidelidade concluída';
    } else {
      return 'Fidelidade pendente';
    }
  };

  const statusFidelidade = calcularFidelidade();

  const calcularValorTotal = () => {
    const valorBase = Number(plano.valor) || 0;
    const valorExamesAdicionais = (plano.exames_adicionais || []).reduce((total, exame) => 
      total + (Number(exame.valor) || 0), 0
    );
    const valorToxicologicos = (plano.exames_toxicologicos || []).reduce((total, toxico) => 
      total + (Number(toxico.valor) || 0), 0
    );

    return valorBase + valorExamesAdicionais + valorToxicologicos;
  };

  const valorTotal = calcularValorTotal();

  return (
    <Card 
      variant="outlined" 
      style={{ 
        border: '2px solid #e0e0e0',
        borderRadius: '12px',
        transition: 'all 0.3s ease',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <CardContent style={{ flex: 1, padding: '24px' }}>
        <Box style={{ textAlign: 'center', marginBottom: '20px' }}>
          <Typography 
            variant="h5" 
            style={{ 
              fontWeight: 'bold',
              color: '#1976d2',
              marginBottom: '8px'
            }}
          >
            Plano {plano.nome}
          </Typography>
          <Typography 
            variant="body2" 
            color="textSecondary"
            style={{ marginBottom: '16px', minHeight: '40px' }}
          >
            {plano.nome === 'Bronze' && 'Plano básico com benefícios essenciais para sua empresa'}
            {plano.nome === 'Prata' && 'Plano intermediário com mais benefícios e coberturas'}
            {plano.nome === 'Ouro' && 'Plano completo com benefícios premium e atendimento especializado'}
            {plano.nome === 'Platina' && 'Plano máximo com todos os benefícios e coberturas exclusivas'}
          </Typography>
        </Box>

        <Box style={{ textAlign: 'center', marginBottom: '20px', padding: '16px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
          <Typography 
            variant="h4" 
            style={{ 
              fontWeight: 'bold',
              color: '#1976d2'
            }}
          >
            R$ {valorTotal.toFixed(2)}
          </Typography>
          <Typography 
            variant="body2" 
            color="textSecondary"
          >
            Valor mensal total do plano
          </Typography>
        </Box>

        <Divider style={{ margin: '16px 0' }} />

        <Box style={{ marginBottom: '20px', textAlign: 'center' }}>
          <Typography 
            variant="subtitle2" 
            style={{ 
              fontWeight: 'bold',
              marginBottom: '8px',
              color: statusFidelidade === 'Em período de fidelidade' ? '#d32f2f' : '#2e7d32'
            }}
          >
            {statusFidelidade}
          </Typography>
          {dataCriacao && dataRenovacao && (
            <Typography variant="caption" color="textSecondary">
              De {new Date(dataCriacao).toLocaleDateString('pt-BR')} até {new Date(dataRenovacao).toLocaleDateString('pt-BR')}
            </Typography>
          )}
        </Box>

        <Box style={{ marginBottom: '20px' }}>
          <Typography variant="subtitle1" style={{ fontWeight: 'bold', marginBottom: '12px' }}>
            Benefícios Inclusos:
          </Typography>

          {plano.exames_fixos && plano.exames_fixos.length > 0 && (
            <Box style={{ marginBottom: '16px' }}>
              <Typography variant="subtitle2" style={{ fontWeight: 'bold', marginBottom: '8px', color: '#1976d2' }}>
                Exames Fixos:
              </Typography>
              {plano.exames_fixos.map((exame, index) => (
                <Box key={index} style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '8px' }}>
                  <Box style={{ 
                    width: '6px', 
                    height: '6px', 
                    backgroundColor: '#1976d2', 
                    borderRadius: '50%',
                    marginRight: '12px',
                    marginTop: '6px'
                  }} />
                  <Typography variant="body2" style={{ lineHeight: 1.4 }}>
                    {exame.nome}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}

          {plano.exames_adicionais && plano.exames_adicionais.length > 0 && (
            <Box style={{ marginBottom: '16px' }}>
              <Typography variant="subtitle2" style={{ fontWeight: 'bold', marginBottom: '8px', color: '#1976d2' }}>
                Exames Adicionais:
              </Typography>
              {plano.exames_adicionais.map((exame, index) => (
                <Box key={index} style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '8px' }}>
                  <Box style={{ 
                    width: '6px', 
                    height: '6px', 
                    backgroundColor: '#1976d2', 
                    borderRadius: '50%',
                    marginRight: '12px',
                    marginTop: '6px'
                  }} />
                  <Typography variant="body2" style={{ lineHeight: 1.4 }}>
                    {exame.nome} - R$ {Number(exame.valor).toFixed(2)}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}

          {plano.servicos && plano.servicos.length > 0 && (
            <Box style={{ marginBottom: '16px' }}>
              <Typography variant="subtitle2" style={{ fontWeight: 'bold', marginBottom: '8px', color: '#1976d2' }}>
                Serviços:
              </Typography>
              {plano.servicos.map((servico, index) => (
                <Box key={index} style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '8px' }}>
                  <Box style={{ 
                    width: '6px', 
                    height: '6px', 
                    backgroundColor: '#1976d2', 
                    borderRadius: '50%',
                    marginRight: '12px',
                    marginTop: '6px'
                  }} />
                  <Typography variant="body2" style={{ lineHeight: 1.4 }}>
                    {servico.nome}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}

          {plano.exames_toxicologicos && plano.exames_toxicologicos.length > 0 && (
            <Box style={{ marginBottom: '16px' }}>
              <Typography variant="subtitle2" style={{ fontWeight: 'bold', marginBottom: '8px', color: '#1976d2' }}>
                Exames Toxicológicos:
              </Typography>
              {plano.exames_toxicologicos.map((toxico, index) => (
                <Box key={index} style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '8px' }}>
                  <Box style={{ 
                    width: '6px', 
                    height: '6px', 
                    backgroundColor: '#1976d2', 
                    borderRadius: '50%',
                    marginRight: '12px',
                    marginTop: '6px'
                  }} />
                  <Typography variant="body2" style={{ lineHeight: 1.4 }}>
                    {toxico.nome} - R$ {Number(toxico.valor).toFixed(2)}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}

          {plano.riscos && plano.riscos.length > 0 && (
            <Box style={{ marginBottom: '16px' }}>
              <Typography variant="subtitle2" style={{ fontWeight: 'bold', marginBottom: '8px', color: '#1976d2' }}>
                Riscos Cobertos:
              </Typography>
              <Box style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                {plano.riscos.map((risco, index) => (
                  <Chip 
                    key={index}
                    label={risco.nome}
                    size="small"
                    variant="outlined"
                    style={{ margin: '2px', backgroundColor: '#e3f2fd' }}
                  />
                ))}
              </Box>
            </Box>
          )}

          <Box style={{ display: 'flex', justifyContent: 'space-between', marginTop: '16px', padding: '12px', backgroundColor: '#f5f5f5', borderRadius: '6px' }}>
            <Typography variant="body2" style={{ fontWeight: 'bold' }}>
              Total de Itens:
            </Typography>
            <Typography variant="body2">
              {(plano.exames_fixos?.length || 0) + (plano.exames_adicionais?.length || 0) + (plano.servicos?.length || 0) + (plano.exames_toxicologicos?.length || 0)}
            </Typography>
          </Box>
        </Box>
      </CardContent>

      <CardActions style={{ padding: '16px 24px', justifyContent: 'flex-end', borderTop: '1px solid #e0e0e0' }}>
        <Tooltip title="Editar Plano">
          <IconButton 
            size="small" 
            onClick={onEdit}
            style={{ marginRight: '8px', color: '#1976d2' }}
          >
            <Pencil size={16} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Excluir Plano">
          <IconButton 
            size="small" 
            onClick={onDelete}
            style={{ color: '#d32f2f' }}
          >
            <Trash size={16} />
          </IconButton>
        </Tooltip>
      </CardActions>
    </Card>
  );
}

function ExamesModal({ 
  open, 
  onClose, 
  onAddExame,
  tipo,
  examesJaAdicionados = []
}: { 
  open: boolean;
  onClose: () => void;
  onAddExame: (exame: IExame) => void;
  tipo: 'fixo' | 'adicional';
  examesJaAdicionados?: IExame[];
}) {
  const [selectedExame, setSelectedExame] = useState<IExame | null>(null);
  const [valorPersonalizado, setValorPersonalizado] = useState<string>('');
  const { data: examesDisponiveis, loading, fetchData } = useLazyFetch<IExame>('/ravim/exames');

  useEffect(() => {
    if (open) {
      fetchData();
    }
  }, [open, fetchData]);

  useEffect(() => {
    if (open) {
      setSelectedExame(null);
      setValorPersonalizado('');
    }
  }, [open]);

  const examesFiltrados = examesDisponiveis.filter(exame => 
    !examesJaAdicionados.some(jaAdicionado => jaAdicionado.id_exames == exame.id_exames)
  );

  const idsJaAdicionados = examesJaAdicionados.map(exame => exame.id_exames);

  const handleAdd = () => {
    if (selectedExame) {
      if (examesJaAdicionados.some(jaAdicionado => jaAdicionado.id_exames == selectedExame.id_exames)) {
        alert('Este exame já foi adicionado anteriormente');
        return;
      }

      if (tipo === 'fixo') {
        onAddExame({
          id_exames: selectedExame.id_exames,
          nome: selectedExame.nome
        });
      } else {
        if (valorPersonalizado) {
          onAddExame({
            id_exames: selectedExame.id_exames,
            nome: selectedExame.nome,
            valor: Number(valorPersonalizado)
          });
        } else {
          alert('Por favor, informe o valor do exame adicional');
          return;
        }
      }
      onClose();
    } else {
      alert('Por favor, selecione um exame');
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="sm" 
      fullWidth
      PaperProps={{
        style: {
          maxHeight: '80vh',
          height: 'auto'
        }
      }}
    >
      <DialogTitle>
        Adicionar Exame {tipo === 'fixo' ? 'Fixo' : 'Adicional'}
      </DialogTitle>
      <DialogContent style={{ padding: '20px' }}>
        <Box style={{ marginBottom: '20px' }}>
          <Autocomplete
            options={examesFiltrados}
            loading={loading}
            getOptionLabel={(option: IExame) => `${option.id_exames} - ${option.nome}`}
            value={selectedExame}
            onChange={(event, newValue) => {
              setSelectedExame(newValue);
            }}
            filterOptions={(options, state) => {
              const inputValue = state.inputValue.toLowerCase();
              return options.filter(option =>
                option.id_exames.toString().toLowerCase().includes(inputValue) ||
                option.nome.toLowerCase().includes(inputValue)
              );
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Buscar exame por código ou nome"
                variant="outlined"
                fullWidth
                placeholder="Digite o código ou nome do exame..."
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <React.Fragment>
                      {loading ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </React.Fragment>
                  ),
                }}
                helperText={
                  loading ? "Carregando exames..." : 
                  examesJaAdicionados.length > 0 ? 
                    `${examesJaAdicionados.length} exame(s) já adicionado(s)` : 
                    "Digite o código ou nome do exame para buscar"
                }
              />
            )}
            noOptionsText={
              loading ? "Carregando..." : 
              "Nenhum exame disponível encontrado"
            }
            getOptionSelected={(option: IExame, value: IExame) => 
              option.id_exames === value.id_exames
            }
            ListboxProps={{
              style: {
                maxHeight: '200px'
              }
            }}
          />
        </Box>

        {examesJaAdicionados.length > 0 && (
          <Box style={{ 
            marginBottom: '16px', 
            padding: '12px', 
            backgroundColor: '#fff3cd', 
            border: '1px solid #ffeaa7',
            borderRadius: '4px'
          }}>
            <Typography variant="body2" style={{ color: '#856404', fontSize: '0.875rem' }}>
              <strong>Exames já adicionados:</strong> {idsJaAdicionados.join(', ')}
            </Typography>
          </Box>
        )}
        
        {tipo === 'adicional' && selectedExame && (
          <Box>
            <TextField
              label="Valor do Exame Adicional"
              type="number"
              value={valorPersonalizado}
              onChange={(e) => setValorPersonalizado(e.target.value)}
              fullWidth
              placeholder="0.00"
              helperText="Informe o valor para este exame adicional"
              InputProps={{
                startAdornment: <Typography style={{ marginRight: 8, color: 'rgba(0, 0, 0, 0.54)' }}>R$</Typography>,
              }}
              inputProps={{
                step: "0.01",
                min: "0"
              }}
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions style={{ padding: '16px 24px' }}>
        <Button title="Cancelar" onClick={onClose} variantstyle="SECUNDARY" />
        <Button 
          title="Adicionar" 
          onClick={handleAdd}
          disabled={!selectedExame || (tipo === 'adicional' && !valorPersonalizado) || loading}
        />
      </DialogActions>
    </Dialog>
  );
}

function ServicosModal({ 
  open, 
  onClose, 
  onAddServico,
  servicosJaAdicionados = []
}: { 
  open: boolean;
  onClose: () => void;
  onAddServico: (servico: IServico) => void;
  servicosJaAdicionados?: IServico[];
}) {
  const [selectedServico, setSelectedServico] = useState<IServico | null>(null);
  const { data: servicosDisponiveis, loading, fetchData } = useLazyFetch<IServico>('/ravim/servicos');

  useEffect(() => {
    if (open) {
      fetchData();
    }
  }, [open, fetchData]);

  useEffect(() => {
    if (open) {
      setSelectedServico(null);
    }
  }, [open]);

  const servicosFiltrados = servicosDisponiveis.filter(servico => 
    !servicosJaAdicionados.some(jaAdicionado => jaAdicionado.id_servicos == servico.id_servicos)
  );

  const idsJaAdicionados = servicosJaAdicionados.map(servico => servico.id_servicos);

  const handleAdd = () => {
    if (selectedServico) {
      if (servicosJaAdicionados.some(jaAdicionado => jaAdicionado.id_servicos == selectedServico.id_servicos)) {
        alert('Este serviço já foi adicionado anteriormente');
        return;
      }

      onAddServico(selectedServico);
      onClose();
    } else {
      alert('Por favor, selecione um serviço');
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="sm" 
      fullWidth
      PaperProps={{
        style: {
          maxHeight: '80vh',
          height: 'auto'
        }
      }}
    >
      <DialogTitle>Adicionar Serviço</DialogTitle>
      <DialogContent style={{ padding: '20px' }}>
        <Box style={{ marginBottom: '20px' }}>
          <Autocomplete
            options={servicosFiltrados}
            loading={loading}
            getOptionLabel={(option: IServico) => `${option.id_servicos} - ${option.nome}`}
            value={selectedServico}
            onChange={(event, newValue) => setSelectedServico(newValue)}
            filterOptions={(options, state) => {
              const inputValue = state.inputValue.toLowerCase();
              return options.filter(option =>
                option.id_servicos.toString().toLowerCase().includes(inputValue) ||
                option.nome.toLowerCase().includes(inputValue)
              );
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Buscar serviço por código ou nome"
                variant="outlined"
                fullWidth
                placeholder="Digite o código ou nome do serviço..."
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <React.Fragment>
                      {loading ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </React.Fragment>
                  ),
                }}
                helperText={
                  loading ? "Carregando serviços..." : 
                  servicosJaAdicionados.length > 0 ? 
                    `${servicosJaAdicionados.length} serviço(s) já adicionado(s)` : 
                    "Digite o código ou nome do serviço para buscar"
                }
              />
            )}
            noOptionsText={
              loading ? "Carregando..." : 
              "Nenhum serviço disponível encontrado"
            }
            getOptionSelected={(option: IServico, value: IServico) => 
              option.id_servicos === value.id_servicos
            }
            ListboxProps={{
              style: {
                maxHeight: '200px'
              }
            }}
          />
        </Box>

        {servicosJaAdicionados.length > 0 && (
          <Box style={{ 
            marginBottom: '16px', 
            padding: '12px', 
            backgroundColor: '#fff3cd', 
            border: '1px solid #ffeaa7',
            borderRadius: '4px'
          }}>
            <Typography variant="body2" style={{ color: '#856404', fontSize: '0.875rem' }}>
              <strong>Serviços já adicionados:</strong> {idsJaAdicionados.join(', ')}
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions style={{ padding: '16px 24px' }}>
        <Button title="Cancelar" onClick={onClose} variantstyle="SECUNDARY" />
        <Button 
          title="Adicionar" 
          onClick={handleAdd}
          disabled={!selectedServico || loading}
        />
      </DialogActions>
    </Dialog>
  );
}

function ToxicologicoModal({ 
  open, 
  onClose, 
  onAddToxicologico,
  toxicologicosJaAdicionados = []
}: { 
  open: boolean;
  onClose: () => void;
  onAddToxicologico: (toxico: IToxicologico) => void;
  toxicologicosJaAdicionados?: IToxicologico[];
}) {
  const [selectedToxicologico, setSelectedToxicologico] = useState<IToxicologico | null>(null);
  const [valorPersonalizado, setValorPersonalizado] = useState<string>('');
  const { data: toxicologicosDisponiveis, loading, fetchData } = useLazyFetch<IToxicologico>('/ravim/exame-tox');

  useEffect(() => {
    if (open) {
      fetchData();
    }
  }, [open, fetchData]);

  useEffect(() => {
    if (open) {
      setSelectedToxicologico(null);
      setValorPersonalizado('');
    }
  }, [open]);

  const toxicologicosFiltrados = toxicologicosDisponiveis.filter(toxico => 
    !toxicologicosJaAdicionados.some(jaAdicionado => jaAdicionado.id_exame_tox == toxico.id_exame_tox)
  );

  const idsJaAdicionados = toxicologicosJaAdicionados.map(toxico => toxico.id_exame_tox);

  const handleAdd = () => {
    if (selectedToxicologico) {
      if (toxicologicosJaAdicionados.some(jaAdicionado => jaAdicionado.id_exame_tox == selectedToxicologico.id_exame_tox)) {
        alert('Este exame toxicológico já foi adicionado anteriormente');
        return;
      }

      if (valorPersonalizado) {
        onAddToxicologico({
          ...selectedToxicologico,
          valor: Number(valorPersonalizado)
        });
      } else {
        alert('Por favor, informe o valor do exame toxicológico');
        return;
      }
      onClose();
    } else {
      alert('Por favor, selecione um exame toxicológico');
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="sm" 
      fullWidth
      PaperProps={{
        style: {
          maxHeight: '80vh',
          height: 'auto'
        }
      }}
    >
      <DialogTitle>Adicionar Exame Toxicológico</DialogTitle>
      <DialogContent style={{ padding: '20px' }}>
        <Box style={{ marginBottom: '20px' }}>
          <Autocomplete
            options={toxicologicosFiltrados}
            loading={loading}
            getOptionLabel={(option: IToxicologico) => `${option.id_exame_tox} - ${option.nome}`}
            value={selectedToxicologico}
            onChange={(event, newValue) => {
              setSelectedToxicologico(newValue);
            }}
            filterOptions={(options, state) => {
              const inputValue = state.inputValue.toLowerCase();
              return options.filter(option =>
                option.id_exame_tox.toString().toLowerCase().includes(inputValue) ||
                option.nome.toLowerCase().includes(inputValue)
              );
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Buscar exame toxicológico por código ou nome"
                variant="outlined"
                fullWidth
                placeholder="Digite o código ou nome do exame..."
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <React.Fragment>
                      {loading ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </React.Fragment>
                  ),
                }}
                helperText={
                  loading ? "Carregando exames..." : 
                  toxicologicosJaAdicionados.length > 0 ? 
                    `${toxicologicosJaAdicionados.length} exame(s) já adicionado(s)` : 
                    "Digite o código ou nome do exame para buscar"
                }
              />
            )}
            noOptionsText={
              loading ? "Carregando..." : 
              "Nenhum exame toxicológico disponível encontrado"
            }
            getOptionSelected={(option: IToxicologico, value: IToxicologico) => 
              option.id_exame_tox === value.id_exame_tox
            }
            ListboxProps={{
              style: {
                maxHeight: '200px'
              }
            }}
          />
        </Box>

        {toxicologicosJaAdicionados.length > 0 && (
          <Box style={{ 
            marginBottom: '16px', 
            padding: '12px', 
            backgroundColor: '#fff3cd', 
            border: '1px solid #ffeaa7',
            borderRadius: '4px'
          }}>
            <Typography variant="body2" style={{ color: '#856404', fontSize: '0.875rem' }}>
              <strong>Exames toxicológicos já adicionados:</strong> {idsJaAdicionados.join(', ')}
            </Typography>
          </Box>
        )}
        
        {selectedToxicologico && (
          <Box>
            <TextField
              label="Valor do Exame Toxicológico"
              type="number"
              value={valorPersonalizado}
              onChange={(e) => setValorPersonalizado(e.target.value)}
              fullWidth
              placeholder="0.00"
              helperText="Informe o valor para este exame toxicológico"
              InputProps={{
                startAdornment: <Typography style={{ marginRight: 8, color: 'rgba(0, 0, 0, 0.54)' }}>R$</Typography>,
              }}
              inputProps={{
                step: "0.01",
                min: "0"
              }}
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions style={{ padding: '16px 24px' }}>
        <Button title="Cancelar" onClick={onClose} variantstyle="SECUNDARY" />
        <Button 
          title="Adicionar" 
          onClick={handleAdd}
          disabled={!selectedToxicologico || !valorPersonalizado || loading}
        />
      </DialogActions>
    </Dialog>
  );
}

function RiscosModal({ 
  open, 
  onClose, 
  onAddRiscos,
  riscosJaAdicionados = []
}: { 
  open: boolean;
  onClose: () => void;
  onAddRiscos: (riscos: IRiscos[]) => void;
  riscosJaAdicionados?: IRiscos[];
}) {
  const [selectedRiscos, setSelectedRiscos] = useState<IRiscos[]>([]);
  const { data: riscosDisponiveis, loading, fetchData } = useLazyFetch<IRiscos>('/ravim/riscos');

  useEffect(() => {
    if (open) {
      fetchData();
    }
  }, [open, fetchData]);

  useEffect(() => {
    if (open) {
      setSelectedRiscos([]);
    }
  }, [open]);

  const riscosFiltrados = riscosDisponiveis.filter(risco => 
    !riscosJaAdicionados.some(jaAdicionado => jaAdicionado.id_risco == risco.id_risco)
  );

  const idsJaAdicionados = riscosJaAdicionados.map(risco => risco.id_risco);

  const handleAdd = () => {
    if (selectedRiscos.length > 0) {
      onAddRiscos(selectedRiscos);
      onClose();
    } else {
      alert('Por favor, selecione pelo menos um risco');
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="sm" 
      fullWidth
      PaperProps={{
        style: {
          maxHeight: '80vh',
          height: 'auto'
        }
      }}
    >
      <DialogTitle>Adicionar Riscos</DialogTitle>
      <DialogContent style={{ padding: '20px' }}>
        <Box style={{ marginBottom: '20px' }}>
          <Autocomplete
            multiple
            options={riscosFiltrados}
            loading={loading}
            getOptionLabel={(option: IRiscos) => `${option.id_risco} - ${option.nome}`}
            value={selectedRiscos}
            onChange={(event, newValue) => {
              setSelectedRiscos(newValue);
            }}
            filterOptions={(options, state) => {
              const inputValue = state.inputValue.toLowerCase();
              return options.filter(option =>
                option.id_risco.toString().toLowerCase().includes(inputValue) ||
                option.nome.toLowerCase().includes(inputValue)
              );
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Buscar riscos por código ou nome"
                variant="outlined"
                fullWidth
                placeholder="Digite o código ou nome do risco..."
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <React.Fragment>
                      {loading ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </React.Fragment>
                  ),
                }}
                helperText={
                  loading ? "Carregando riscos..." : 
                  riscosJaAdicionados.length > 0 ? 
                    `${riscosJaAdicionados.length} risco(s) já adicionado(s)` : 
                    "Digite o código ou nome do risco para buscar"
                }
              />
            )}
            noOptionsText={
              loading ? "Carregando..." : 
              "Nenhum risco disponível encontrado"
            }
            getOptionSelected={(option: IRiscos, value: IRiscos) => 
              option.id_risco === value.id_risco
            }
            ListboxProps={{
              style: {
                maxHeight: '200px'
              }
            }}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  label={`${option.id_risco} - ${option.nome}`}
                  size="small"
                  {...getTagProps({ index })}
                  style={{ margin: '2px' }}
                />
              ))
            }
          />
        </Box>

        {selectedRiscos.length > 0 && (
          <Box style={{ 
            marginBottom: '16px', 
            padding: '12px', 
            backgroundColor: '#e8f5e8', 
            border: '1px solid #c8e6c9',
            borderRadius: '4px'
          }}>
            <Typography variant="body2" style={{ color: '#2e7d32', fontSize: '0.875rem' }}>
              <strong>{selectedRiscos.length} risco(s) selecionado(s):</strong> {selectedRiscos.map(r => r.id_risco).join(', ')}
            </Typography>
          </Box>
        )}

        {riscosJaAdicionados.length > 0 && (
          <Box style={{ 
            marginBottom: '16px', 
            padding: '12px', 
            backgroundColor: '#fff3cd', 
            border: '1px solid #ffeaa7',
            borderRadius: '4px'
          }}>
            <Typography variant="body2" style={{ color: '#856404', fontSize: '0.875rem' }}>
              <strong>Riscos já adicionados:</strong> {idsJaAdicionados.join(', ')}
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions style={{ padding: '16px 24px' }}>
        <Button title="Cancelar" onClick={onClose} variantstyle="SECUNDARY" />
        <Button 
          title={`Adicionar ${selectedRiscos.length} Risco(s)`}
          onClick={handleAdd}
          disabled={selectedRiscos.length === 0 || loading}
        />
      </DialogActions>
    </Dialog>
  );
}

function PlanoModal({ 
  open, 
  onClose, 
  onSave, 
  plano
}: { 
  open: boolean;
  onClose: () => void;
  onSave: (plano: PlanoFormData) => void;
  plano?: PlanoFormData;
}) {
  const [examesDialog, setExamesDialog] = useState(false);
  const [servicosDialog, setServicosDialog] = useState(false);
  const [toxicologicoDialog, setToxicologicoDialog] = useState(false);
  const [riscosDialog, setRiscosDialog] = useState(false);
  const [tipoExame, setTipoExame] = useState<'fixo' | 'adicional'>('fixo');

  const {
    register,
    handleSubmit,
    control,
    setValue,
    watch,
    formState: { errors }
  } = useForm<PlanoFormData>({
    resolver: zodResolver(planoShema),
    defaultValues: plano || {
      nome: 'Bronze',
      valor: '',
      exames_fixos: [],
      exames_adicionais: [],
      servicos: [],
      exames_toxicologicos: [],
      riscos: []
    }
  });

  const { 
    fields: examesFixosFields, 
    append: appendExameFixo, 
    remove: removeExameFixo 
  } = useFieldArray({
    control,
    name: 'exames_fixos'
  });

  const { 
    fields: examesAdicionaisFields, 
    append: appendExameAdicional, 
    remove: removeExameAdicional 
  } = useFieldArray({
    control,
    name: 'exames_adicionais'
  });

  const { 
    fields: servicosFields, 
    append: appendServico, 
    remove: removeServico 
  } = useFieldArray({
    control,
    name: 'servicos'
  });

  const { 
    fields: toxicologicosFields, 
    append: appendToxicologico, 
    remove: removeToxicologico 
  } = useFieldArray({
    control,
    name: 'exames_toxicologicos'
  });

  const { 
    fields: riscosFields, 
    append: appendRisco, 
    remove: removeRisco 
  } = useFieldArray({
    control,
    name: 'riscos'
  });

  const handleAddExame = (exame: IExame, tipo: 'fixo' | 'adicional') => {
    if (tipo === 'fixo') {
      appendExameFixo({
        id_exames: exame.id_exames,
        nome: exame.nome
      });
    } else {
      appendExameAdicional({
        id_exames: exame.id_exames,
        nome: exame.nome,
        valor: exame.valor ?? 0
      });
    }
    setExamesDialog(false);
  };

  const handleAddServico = (servico: IServico) => {
    appendServico({
      id_servicos: servico.id_servicos,
      nome: servico.nome
    });
    setServicosDialog(false);
  };

  const handleAddToxicologico = (toxico: IToxicologico) => {
    appendToxicologico({
      ...toxico,
      valor: toxico.valor ?? 0
    });
    setToxicologicoDialog(false);
  };

  const handleAddRiscos = (novosRiscos: IRiscos[]) => {
    novosRiscos.forEach(risco => {
      appendRisco({
        id_risco: risco.id_risco,
        nome: risco.nome
      });
    });
    setRiscosDialog(false);
  };

  const calcularValorTotal = () => {
    const valorTotal = watch('valor') || 0;
    const examesAdicionais = watch('exames_adicionais') || [];
    const toxicologicos = watch('exames_toxicologicos') || [];

    const valorExamesAdicionais = examesAdicionais.reduce((total, exame) => 
      total + (Number(exame.valor) || 0), 0
    );
    const valorToxicologicos = toxicologicos.reduce((total, toxico) => 
      total + (Number(toxico.valor) || 0), 0
    );

    return {
      base: Number(valorTotal),
      examesAdicionais: valorExamesAdicionais,
      toxicologicos: valorToxicologicos,
      final: Number(valorTotal) + valorExamesAdicionais + valorToxicologicos
    };
  };

  const valores = calcularValorTotal();

  const handleSave = (planoData: PlanoFormData) => {
    console.log('Dados completos do plano:', planoData);
    onSave(planoData);
  };

  useEffect(() => {
    if (open && plano) {
      setValue('nome', plano.nome);
      setValue('valor', plano.valor);
    } else if (open) {
      setValue('nome', 'Bronze');
      setValue('valor', '');
    }
  }, [open, plano, setValue]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        {plano ? 'Editar Plano' : 'Adicionar Plano'}
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card variant="outlined" style={{ backgroundColor: '#f8f9fa', marginBottom: '16px' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Preview do Plano {watch('nome')}
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Box style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                      <Typography variant="body2" style={{ fontWeight: 'bold', marginRight: '8px', minWidth: '100px' }}>
                        Valor Base:
                      </Typography>
                      <Typography variant="body2">R$ {valores.base.toFixed(2)}</Typography>
                    </Box>
                    <Box style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                      <Typography variant="body2" style={{ fontWeight: 'bold', marginRight: '8px', minWidth: '100px' }}>
                        Valor Total:
                      </Typography>
                      <Typography variant="body2" style={{ color: '#1976d2', fontWeight: 'bold' }}>
                        R$ {valores.final.toFixed(2)}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                      <Typography variant="body2" style={{ fontWeight: 'bold', marginRight: '8px', minWidth: '100px' }}>
                        Itens Inclusos:
                      </Typography>
                      <Typography variant="body2">
                        {examesFixosFields.length + examesAdicionaisFields.length + servicosFields.length + toxicologicosFields.length}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth margin="normal">
              <InputLabel>Nome do Plano</InputLabel>
              <Select
                {...register('nome')}
                defaultValue={plano?.nome || 'Bronze'}
                label="Nome do Plano"
              >
                {OPCOES_PLANOS.map((opcao) => (
                  <MenuItem key={opcao.nome} value={opcao.nome}>
                    {opcao.nome} - {opcao.descricao}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {errors.nome && (
              <Typography variant="caption" color="error">
                {errors.nome.message}
              </Typography>
            )}
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Valor Base do Plano"
              type="number"
              {...register('valor', { valueAsNumber: true })}
              error={!!errors.valor}
              helperText={errors.valor?.message}
              margin="normal"
              InputProps={{
                startAdornment: <Typography style={{ marginRight: 8 }}>R$</Typography>,
              }}
            />
          </Grid>

          <Grid item xs={12}>
            <Card variant="outlined">
              <CardContent>
                <Box style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                  <Typography variant="h6">
                    Exames Fixos
                  </Typography>
                  <Button
                    title="Adicionar Exame Fixo"
                    onClick={() => {
                      setTipoExame('fixo');
                      setExamesDialog(true);
                    }}
                    icon={<Plus size={16} />}
                    type="button"
                    size="small"
                  />
                </Box>
                <Grid container spacing={1}>
                  {examesFixosFields.map((field, index) => (
                    <Grid item xs={12} sm={6} md={4} key={field.id}>
                      <Card variant="outlined" style={{ padding: 8 }}>
                        <Box style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Box>
                            <Typography variant="body2" style={{ fontWeight: 'bold' }}>
                              {field.nome}
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                              ID: {field.id_exames}
                            </Typography>
                          </Box>
                          <IconButton 
                            size="small" 
                            onClick={() => removeExameFixo(index)}
                            style={{ color: 'red' }}
                          >
                            <Trash size={14} />
                          </IconButton>
                        </Box>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12}>
            <Card variant="outlined">
              <CardContent>
                <Box style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                  <Typography variant="h6">
                    Exames Adicionais (Valor: R$ {valores.examesAdicionais.toFixed(2)})
                  </Typography>
                  <Button
                    title="Adicionar Exame Adicional"
                    onClick={() => {
                      setTipoExame('adicional');
                      setExamesDialog(true);
                    }}
                    icon={<Plus size={16} />}
                    type="button"
                    size="small"
                  />
                </Box>
                <Grid container spacing={1}>
                  {examesAdicionaisFields.map((field, index) => (
                    <Grid item xs={12} sm={6} md={4} key={field.id}>
                      <Card variant="outlined" style={{ padding: 8 }}>
                        <Box style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Box>
                            <Typography variant="body2" style={{ fontWeight: 'bold' }}>
                              {field.nome}
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                              ID: {field.id_exames} - R$ {Number(field.valor).toFixed(2)}
                            </Typography>
                          </Box>
                          <IconButton 
                            size="small" 
                            onClick={() => removeExameAdicional(index)}
                            style={{ color: 'red' }}
                          >
                            <Trash size={14} />
                          </IconButton>
                        </Box>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12}>
            <Card variant="outlined">
              <CardContent>
                <Box style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                  <Typography variant="h6">
                    Serviços
                  </Typography>
                  <Button
                    title="Adicionar Serviço"
                    onClick={() => setServicosDialog(true)}
                    icon={<Plus size={16} />}
                    type="button"
                    size="small"
                  />
                </Box>
                <Grid container spacing={1}>
                  {servicosFields.map((field, index) => (
                    <Grid item xs={12} sm={6} md={4} key={field.id}>
                      <Card variant="outlined" style={{ padding: 8 }}>
                        <Box style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Box>
                            <Typography variant="body2" style={{ fontWeight: 'bold' }}>
                              {field.nome}
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                              ID: {field.id_servicos}
                            </Typography>
                          </Box>
                          <IconButton 
                            size="small" 
                            onClick={() => removeServico(index)}
                            style={{ color: 'red' }}
                          >
                            <Trash size={14} />
                          </IconButton>
                        </Box>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12}>
            <Card variant="outlined">
              <CardContent>
                <Box style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                  <Typography variant="h6">
                    Exames Toxicológicos (Valor: R$ {valores.toxicologicos.toFixed(2)})
                  </Typography>
                  <Button
                    title="Adicionar Toxicológico"
                    onClick={() => setToxicologicoDialog(true)}
                    icon={<Plus size={16} />}
                    type="button"
                    size="small"
                  />
                </Box>
                <Grid container spacing={1}>
                  {toxicologicosFields.map((field, index) => (
                    <Grid item xs={12} sm={6} md={4} key={field.id}>
                      <Card variant="outlined" style={{ padding: 8 }}>
                        <Box style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Box>
                            <Typography variant="body2" style={{ fontWeight: 'bold' }}>
                              {field.nome}
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                              ID: {field.id_exame_tox} - R$ {Number(field.valor).toFixed(2)}
                            </Typography>
                          </Box>
                          <IconButton 
                            size="small" 
                            onClick={() => removeToxicologico(index)}
                            style={{ color: 'red' }}
                          >
                            <Trash size={14} />
                          </IconButton>
                        </Box>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12}>
            <Card variant="outlined">
              <CardContent>
                <Box style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                  <Typography variant="h6">
                    Riscos
                  </Typography>
                  <Button
                    title="Adicionar Riscos"
                    onClick={() => setRiscosDialog(true)}
                    icon={<Plus size={16} />}
                    type="button"
                    size="small"
                  />
                </Box>
                <Grid container spacing={1}>
                  {riscosFields.map((field, index) => (
                    <Grid item xs={12} sm={6} md={4} key={field.id}>
                      <Card variant="outlined" style={{ padding: 8 }}>
                        <Box style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Box>
                            <Typography variant="body2" style={{ fontWeight: 'bold' }}>
                              {field.nome}
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                              ID: {field.id_risco}
                            </Typography>
                          </Box>
                          <IconButton 
                            size="small" 
                            onClick={() => removeRisco(index)}
                            style={{ color: 'red' }}
                          >
                            <Trash size={14} />
                          </IconButton>
                        </Box>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12}>
            <Card variant="outlined" style={{ backgroundColor: '#1976d2', color: 'white' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Resumo do Valor Mensal
                </Typography>
                <Grid container spacing={1}>
                  <Grid item xs={6}>
                    <Typography>Valor Base:</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography style={{ textAlign: 'right' }}>R$ {valores.base.toFixed(2)}</Typography>
                  </Grid>
                  
                  <Grid item xs={6}>
                    <Typography>Exames Adicionais:</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography style={{ textAlign: 'right' }}>R$ {valores.examesAdicionais.toFixed(2)}</Typography>
                  </Grid>
                  
                  <Grid item xs={6}>
                    <Typography>Exames Toxicológicos:</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography style={{ textAlign: 'right' }}>R$ {valores.toxicologicos.toFixed(2)}</Typography>
                  </Grid>
                  
                  <Grid item xs={6}>
                    <Typography variant="h6">VALOR FINAL:</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="h6" style={{ textAlign: 'right' }}>
                      R$ {valores.final.toFixed(2)}
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <ExamesModal
          open={examesDialog && tipoExame === 'fixo'}
          onClose={() => setExamesDialog(false)}
          onAddExame={(exame) => handleAddExame(exame, tipoExame)}
          tipo={tipoExame}
          examesJaAdicionados={[...examesFixosFields, ...examesAdicionaisFields]}
        />
        <ExamesModal
          open={examesDialog && tipoExame === 'adicional'}
          onClose={() => setExamesDialog(false)}
          onAddExame={(exame) => handleAddExame(exame, tipoExame)}
          tipo={tipoExame}
          examesJaAdicionados={[...examesFixosFields, ...examesAdicionaisFields]}
        />

        <ServicosModal
          open={servicosDialog}
          onClose={() => setServicosDialog(false)}
          onAddServico={handleAddServico}
          servicosJaAdicionados={servicosFields}
        />

        <ToxicologicoModal
          open={toxicologicoDialog}
          onClose={() => setToxicologicoDialog(false)}
          onAddToxicologico={handleAddToxicologico}
          toxicologicosJaAdicionados={toxicologicosFields}
        />

        <RiscosModal
          open={riscosDialog}
          onClose={() => setRiscosDialog(false)}
          onAddRiscos={handleAddRiscos}
          riscosJaAdicionados={riscosFields}
        />

      </DialogContent>
      <DialogActions>
        <Button title="Cancelar" onClick={onClose} variantstyle="SECUNDARY" />
        <Button 
          title="Salvar Plano" 
          onClick={handleSubmit(handleSave)}
        />
      </DialogActions>
    </Dialog>
  );
}

function CadContratos() {
  const { user } = useAuth();
  const [risco, setRisco] = useState<IRiscos[]>([]);

  const [snack, setSnack] = useState(false);
  const [message, setMessage] = useState('');
  const [severity, setSeverity] = useState<Color | undefined>();
  const [cod, setCod] = useState('');

  const [planoModalOpen, setPlanoModalOpen] = useState(false);
  const [planoEditando, setPlanoEditando] = useState<(PlanoFormData & { _index?: number }) | null>(null);

  const { data: riscosData, loading: riscosLoading, fetchData: fetchRiscos } = useLazyFetch<IRiscos>('/ravim/riscos');

  const hora = new Date();
  const newHora = hora.toLocaleString('pt-BR', { hour12: false });
  const userUpdate = `${user.nome} ${newHora}`;

  const {
    register,
    handleSubmit,
    reset,
    control,
    setValue,
    getValues,
    watch,
    formState: { errors, isSubmitting, isValid }
  } = useForm<ContractSaveFormData>({
    resolver: zodResolver(contractShema),
    defaultValues: {
      usuario: userUpdate,
      planos: [],
      risco: []
    }
  });

  const { fields: planosFields, append: appendPlano, remove: removePlano, update: updatePlano } = useFieldArray({
    control,
    name: 'planos'
  });

  const { fields: riscoFields, append: appendRisco, remove: removeRisco } = useFieldArray({
    control,
    name: 'risco'
  });

  const {
    uploadedFiles: files,
    fetchDataFiles,
    receivedCodContrato
  } = useFiles();

  const filesNames = files.map((file) => {
    return file.name;
  });

  useEffect(() => {
    if (filesNames) {
      setValue('arquivo', filesNames);
    }
  }, [files]);

  useEffect(() => {
    fetchDataFiles([]);
  }, []);

  useEffect(() => {
    receivedCodContrato(getValues('cod_contrato'));
  }, [getValues('cod_contrato')]);

  function handleAddRiscos(riscos: IRiscos[]) {
    setRisco(riscos);
    
    while (riscoFields.length > 0) {
      removeRisco(0);
    }
    
    riscos.forEach(riscoItem => {
      appendRisco({
        id_risco: riscoItem.id_risco,
        nome: riscoItem.nome
      });
    });
  }

  const handleOpenPlanoModal = (planoIndex?: number) => {
    if (planoIndex !== undefined) {
      setPlanoEditando({ 
        ...planosFields[planoIndex],
        _index: planoIndex
      });
    } else {
      setPlanoEditando(null);
    }
    setPlanoModalOpen(true);
  };

  const handleSavePlano = (planoData: PlanoFormData) => {
    console.log('Salvando plano completo:', planoData);
    if (planoEditando && planoEditando._index !== undefined) {
      updatePlano(planoEditando._index, planoData);
    } else {
      appendPlano(planoData);
    }
    setPlanoModalOpen(false);
    setPlanoEditando(null);
  };

  const handleDeletePlano = (index: number) => {
    removePlano(index);
  };

  async function cadastroEmpresas(data: ContractSaveFormData) {
    console.log('Dados completos do contrato:', data);

    try {
      const res = await api.post(`/ravim/cad_emp`, data);
      if (res.status === 200) {
        setSnack(true);
        setCod(String(res.status));
        setMessage(res.data.message);
        setSeverity('success');
        setTimeout(() => {
          window.location.replace('/contratos-empresa');
        }, 2000);
      } else {
        setSnack(true);
        setCod(String(res.status));
        setMessage(res.data.message);
        setSeverity('error');
      }
    } catch (error) {
      if (error instanceof AppError) {
        setSnack(true);
        setCod(String(error.code));
        setMessage(error.message);
        setSeverity('error');
      }
    }
  }

  const dataCriacao = watch('data_criacao');
  const dataRenovacao = watch('data_renovacao');

  return (
    <Container>
      <Typography variant="h4" align="center" style={{ marginBottom: '32px', fontWeight: 'bold' }}>
        Cadastro de Contrato de Empresas
      </Typography>
      <form onSubmit={handleSubmit(cadastroEmpresas)}>
        <PaperContainer>
          <Typography variant="h5" style={{ marginBottom: '16px', color: '#1976d2' }}>
            Dados do Cadastro
          </Typography>
          <Grid container spacing={1}>
            <Grid item xs>
              <DivGridFieldDataContract>
                <TextField
                  fullWidth
                  variant="outlined"
                  label="Nome do contrato"
                  {...register('nome_contrato')}
                />
                <TextField
                  fullWidth
                  variant="outlined"
                  label="Código do Contrato"
                  {...register('cod_contrato')}
                  error={!!errors.cod_contrato?.message}
                  helperText={errors.cod_contrato?.message}
                />

                <TextField
                  id="date"
                  label="Data de Criação"
                  type="date"
                  variant="outlined"
                  fullWidth
                  InputLabelProps={{
                    shrink: true
                  }}
                  {...register('data_criacao')}
                />
                <TextField
                  id="date"
                  label="Data de Renovação"
                  type="date"
                  fullWidth
                  variant="outlined"
                  InputLabelProps={{
                    shrink: true
                  }}
                  {...register('data_renovacao')}
                />
              </DivGridFieldDataContract>

              <Upload />
              <FileList />
            </Grid>
          </Grid>

          <Typography variant="h5" style={{ marginBottom: '16px', marginTop: '24px', color: '#1976d2' }}>
            Benefícios
          </Typography>

          <DivBenefits>
            <TextField
              fullWidth
              variant="outlined"
              label="Nº Convênio Médico"
              {...register('num_conv_med')}
            />
            <TextField
              fullWidth
              variant="outlined"
              label="Nº Convênio Odontológico"
              {...register('num_conv_odont')}
            />

            <TextField
              fullWidth
              variant="outlined"
              label="Nº Convênio Academia"
              {...register('num_conv_acade')}
            />
            <TextField
              fullWidth
              variant="outlined"
              label="Nº Seguro de Vida"
              {...register('num_seg_vida')}
            />
            <TextField
              fullWidth
              variant="outlined"
              label="Nº Apólice PAT"
              {...register('num_apol_pat')}
            />
          </DivBenefits>

          <Typography variant="h5" style={{ marginBottom: '16px', marginTop: '24px', color: '#1976d2' }}>
            Plano do Contrato
          </Typography>
          
          <Box style={{ marginBottom: 24 }}>
            <Button
              title={planosFields.length === 0 ? "Adicionar Plano" : "Editar Plano"}
              onClick={() => handleOpenPlanoModal()}
              icon={<Plus size={20} />}
              type="button"
              disabled={planosFields.length > 0 && !planoEditando}
            />
            {planosFields.length > 0 && (
              <Typography variant="body2" color="textSecondary" style={{ marginTop: '8px' }}>
                O contrato pode ter apenas um plano. Edite o plano existente ou exclua para criar um novo.
              </Typography>
            )}
          </Box>

          <Grid container spacing={3}>
            {planosFields.map((plano, index) => (
              <Grid item xs={12} key={plano.id}>
                <PlanoCard
                  plano={plano}
                  onEdit={() => handleOpenPlanoModal(index)}
                  onDelete={() => handleDeletePlano(index)}
                  dataCriacao={dataCriacao}
                  dataRenovacao={dataRenovacao}
                />
              </Grid>
            ))}
            {planosFields.length === 0 && (
              <Grid item xs={12}>
                <Card variant="outlined" style={{ textAlign: 'center', padding: '40px' }}>
                  <Typography variant="h6" color="textSecondary">
                    Nenhum plano cadastrado
                  </Typography>
                  <Typography variant="body2" color="textSecondary" style={{ marginTop: '8px' }}>
                    Clique em "Adicionar Plano" para criar o plano do contrato
                  </Typography>
                </Card>
              </Grid>
            )}
          </Grid>

          {/* <Typography variant="h5" style={{ marginBottom: '16px', marginTop: '24px', color: '#1976d2' }}>
            Riscos do Contrato
          </Typography>

          <div>
            <Autocomplete
              multiple
              id="tags-outlined"
              options={riscosData}
              loading={riscosLoading}
              onOpen={() => {
                if (riscosData.length === 0) {
                  fetchRiscos();
                }
              }}
              getOptionLabel={(option: IRiscos) => {
                return `${option.id_risco} - ${option.nome}`
              }}
              onChange={(e: any, newValue, reason, detail) => {
                handleAddRiscos(newValue);
              }}
              value={risco}
              filterSelectedOptions
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  label="Riscos"
                  placeholder="Selecione os riscos"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <React.Fragment>
                        {riscosLoading ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </React.Fragment>
                    ),
                  }}
                  helperText={riscosLoading ? "Carregando riscos..." : "Selecione os riscos do contrato"}
                />
              )}
              noOptionsText={riscosLoading ? "Carregando..." : "Nenhum risco encontrado"}
            />
          </div> */}
        </PaperContainer>

        <DivButtons>
          <NavLink to="/contratos-empresa">
            <Button title="Sair" variantstyle="RED" />
          </NavLink>
          <Button
            type="submit"
            title="Salvar Contrato"
            isLoading={isSubmitting}
            disabled={!isValid || planosFields.length === 0}
          />
        </DivButtons>
      </form>

      <PlanoModal
        open={planoModalOpen}
        onClose={() => {
          setPlanoModalOpen(false);
          setPlanoEditando(null);
        }}
        onSave={handleSavePlano}
        plano={planoEditando || undefined}
      />

      <Snackbar
        open={snack}
        autoHideDuration={2000}
        onClose={() => {
          setSnack(!snack);
        }}
      >
        <MuiAlert
          severity={severity}
          variant="filled"
        >{`${cod} - ${message}`}</MuiAlert>
      </Snackbar>
    </Container>
  );
}

export default CadContratos;